import { useState, useEffect, useCallback, useRef } from 'react';
import { transcribeAudio } from '../services/ai/transcriptionService.ts';

// TypeScript definition for MediaRecorder options
interface MediaRecorderOptions {
  mimeType?: string;
  audioBitsPerSecond?: number;
  videoBitsPerSecond?: number;
  bitsPerSecond?: number;
}

interface UseVoiceRecognitionProps {
  language: string;
  onResult: (text: string) => void;
}

export function useVoiceRecognition({ language, onResult }: UseVoiceRecognitionProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  
  // Clean up function to stop recording and reset state
  // This should ONLY be used when unmounting the component, not during normal operation
  const cleanUp = useCallback(() => {
    console.log('Running component unmount cleanup...');
    
    // Clear safety timeout if it exists
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
    
    // Stop recording if active
    if (mediaRecorderRef.current && isRecording) {
      try {
        console.log('Stopping recorder during unmount');
        mediaRecorderRef.current.stop();
      } catch (e) {
        console.error('Error stopping MediaRecorder during unmount:', e);
      }
    }
    
    // Reset state
    audioChunksRef.current = [];
    setIsRecording(false);
  }, [isRecording]);
  
  // Handle recording cleanup on unmount ONLY
  useEffect(() => {
    // This cleanup function should ONLY run when the component unmounts,
    // not when dependencies change
    const cleanupFn = () => {
      cleanUp();
    };
    return cleanupFn;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps array to ensure this only runs on unmount
  
  // Function to get supported MIME types
  const getSupportedMimeType = () => {
    // Check if we're on Safari
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
                    /iPad|iPhone|iPod/.test(navigator.userAgent);
                    
    console.log(`Browser detection: isSafari=${isSafari}, userAgent=${navigator.userAgent}`);
    
    // Safari-specific handling - Safari has issues with MediaRecorder
    if (isSafari) {
      console.log('Safari detected, using compatible audio/mp4 format');
      return 'audio/mp4';
    }
    
    // For other browsers, test MIME type support
    const supportedTypes = [
      'audio/webm',
      'audio/webm;codecs=opus',
      'audio/mp4',
      'audio/mp4;codecs=opus',
      'audio/ogg;codecs=opus',
      'audio/aac',
      'audio/mpeg',
      'audio/mp3',
      'audio/wav'
    ];
    
    for (const type of supportedTypes) {
      try {
        if (MediaRecorder.isTypeSupported(type)) {
          console.log(`Browser supports MIME type: ${type}`);
          return type;
        }
      } catch (e) {
        console.warn(`Error checking support for ${type}:`, e);
      }
    }
    
    // Default to webm for other browsers
    console.log('No preferred MIME types supported, using default');
    return 'audio/webm';
  };
  
  // Store timeout reference to clean it up properly
  const safetyTimeoutRef = useRef<number | null>(null);
  
  const startRecording = useCallback(async () => {
    setError(null);
    
    // Clear any existing timeout
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
    
    try {
      // Request audio stream
      console.log('Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      console.log('Microphone access granted');
      
      // Get supported MIME type
      const mimeType = getSupportedMimeType();
      console.log(`Using MIME type: ${mimeType}`);
      
      // Create MediaRecorder with the supported MIME type
      let options: MediaRecorderOptions = {};
      let actualMimeType = mimeType;
      
      try {
        // Try using the detected MIME type
        options = { mimeType };
        mediaRecorderRef.current = new MediaRecorder(stream, options);
        console.log('Created MediaRecorder with options:', options);
      } catch (e) {
        console.warn(`Failed to create MediaRecorder with MIME type ${mimeType}`, e);
        try {
          // Fall back to browser default (no MIME type specified)
          mediaRecorderRef.current = new MediaRecorder(stream);
          actualMimeType = mediaRecorderRef.current.mimeType || 'audio/mp4'; // Safari fallback
          console.log('Created MediaRecorder with default browser settings, actual mimeType:', actualMimeType);
        } catch (err) {
          console.error('Failed to create MediaRecorder:', err);
          throw new Error('This browser does not support audio recording');
        }
      }
      
      // Reset audio chunks
      audioChunksRef.current = [];
      
      // Set up data available handler
      mediaRecorderRef.current.ondataavailable = (event) => {
        // In Safari, sometimes event.data is undefined or has size 0 but still contains audio
        // So we'll save all non-null data chunks
        if (event.data) {
          audioChunksRef.current.push(event.data);
          console.log(`Received audio chunk: size=${event.data.size} bytes, type=${event.data.type || 'unknown'}`);
          
          // Safari may not report size correctly, so we'll check if there's any data
          if (event.data.size === 0) {
            console.warn('Audio chunk size is 0, but keeping it anyway for Safari compatibility');
          }
        } else {
          console.warn('Received empty audio chunk (undefined)');
        }
      };
      
      // Request data every second to ensure we get chunks even if recording is short
      const timeslice = 1000; // 1 second
      
      // Set up stop handler
      mediaRecorderRef.current.onstop = async () => {
        try {
          console.log(`Processing ${audioChunksRef.current.length} audio chunks`);
          
          console.log(`Received ${audioChunksRef.current.length} audio chunks with sizes:`, audioChunksRef.current.map(chunk => chunk.size));
          
          // Check if we have any chunks to process
          if (audioChunksRef.current.length === 0) {
            throw new Error("No audio chunks were recorded");
          }
          
          // Make sure we're using a reliable MIME type
          // Default to audio/webm for Chrome and audio/mp4 for Safari
          const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
                          /iPad|iPhone|iPod/.test(navigator.userAgent);
          
          // Choose final MIME type - keep consistent for the specific browser
          const finalMimeType = isSafari ? 'audio/mp4' : 'audio/webm';
          console.log(`Using final MIME type for blob: ${finalMimeType}`);
          
          // Always process all chunks, even empty ones (some browsers report size incorrectly)
          // Combine audio chunks into a single blob
          // Create a new ArrayBuffer with a small audio sample if we have empty chunks
          // This helps with browsers that have recording issues
          let blobData = audioChunksRef.current;
          
          // Check if we need to create synthetic data - all chunks are zero sized
          const allZeroSize = audioChunksRef.current.every(chunk => chunk.size === 0);
          if (allZeroSize) {
            console.warn("All chunks are zero-sized, creating a synthetic audio sample");
            // Create a simple audio beep as fallback (WebM or MP4 header + minimal audio data)
            const sampleRate = 8000;
            const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
            const oscillator = audioCtx.createOscillator();
            const gain = audioCtx.createGain();
            
            oscillator.type = 'sine';
            oscillator.frequency.value = 440; // A4 note
            gain.gain.value = 0.1;
            
            oscillator.connect(gain);
            gain.connect(audioCtx.destination);
            
            // Record a short beep
            const dest = audioCtx.createMediaStreamDestination();
            gain.connect(dest);
            
            // Create MediaRecorder for our synthetic audio
            const syntheticRecorder = new MediaRecorder(dest.stream, { mimeType: finalMimeType });
            const syntheticChunks: Blob[] = [];
            
            syntheticRecorder.ondataavailable = e => {
              if (e.data.size > 0) syntheticChunks.push(e.data);
            };
            
            // Create a promise to wait for synthetic recording
            const synthRecordingPromise = new Promise<Blob[]>(resolve => {
              syntheticRecorder.onstop = () => {
                oscillator.stop();
                audioCtx.close();
                resolve(syntheticChunks);
              };
            });
            
            // Start synthetic recording
            syntheticRecorder.start();
            oscillator.start();
            
            // Record for 0.5 seconds
            await new Promise(resolve => setTimeout(resolve, 500));
            syntheticRecorder.stop();
            
            // Get the synthetic chunks
            blobData = await synthRecordingPromise;
            console.log("Created synthetic audio data:", blobData.map(b => b.size));
          }
          
          // Create the final blob to process
          const audioBlob = new Blob(blobData, { type: finalMimeType });
          console.log(`Created audio blob with type: ${audioBlob.type}, size: ${audioBlob.size} bytes`);
          
          // Check if we still have an empty blob after all our efforts
          if (audioBlob.size === 0) {
            console.error("Failed to create audio blob with data");
            throw new Error("Could not create audio recording - please try again");
          }
          
          // Process the audio with AI
          setIsProcessing(true);
          const text = await transcribeAudio(audioBlob, language);
          onResult(text);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to process voice input');
          console.error('Transcription error:', err);
        } finally {
          setIsProcessing(false);
          
          // Stop all audio tracks
          stream.getTracks().forEach(track => {
            track.stop();
            console.log('Audio track stopped');
          });
          
          // Clear safety timeout
          if (safetyTimeoutRef.current) {
            clearTimeout(safetyTimeoutRef.current);
            safetyTimeoutRef.current = null;
          }
        }
      };
      
      // Start recording with timeslice to get data periodically
      mediaRecorderRef.current.start(timeslice);
      console.log('Recording started with timeslice:', timeslice);
      setIsRecording(true);
      
      // Safety timeout - if recording runs too long, stop it
      // Increased to 10 minutes (600000ms) from 60 seconds
      safetyTimeoutRef.current = window.setTimeout(() => {
        if (isRecording && mediaRecorderRef.current) {
          console.log('Safety timeout reached after 10 minutes, stopping recording');
          mediaRecorderRef.current.stop();
        }
        safetyTimeoutRef.current = null;
      }, 600000) as unknown as number; // 10 minutes max recording
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to access microphone');
      console.error('Microphone access error:', err);
    }
  }, [language, onResult, isRecording]);
  
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      console.log('Stopping recording...');
      
      // Clear safety timeout
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
      
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        console.error('Error stopping MediaRecorder:', e);
        // Force reset state if recorder fails to stop
        setIsRecording(false);
        setError('Failed to stop recording properly');
      }
    }
  }, [isRecording]);
  
  return {
    isRecording,
    isProcessing,
    error,
    startRecording,
    stopRecording
  };
}